import React, { useState, useEffect, useRef } from 'react';
import { Box, Card, Typography, Button } from '@mui/material';
import { keyframes } from '@mui/system';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(85, 104, 254, 0.7);
  }
  50% {
    box-shadow: 0 0 0 15px rgba(85, 104, 254, 0);
  }
`;

const LeaksSpotlight = ({ allowShow = true }) => {
  const [visible, setVisible] = useState(false);
  const [buttonPosition, setButtonPosition] = useState(null);
  const SPOTLIGHT_DISMISS_KEY = 'leaks_spotlight_dismissed';

  useEffect(() => {
    const dismissed = localStorage.getItem(SPOTLIGHT_DISMISS_KEY);
    if (!dismissed && allowShow) {
      setTimeout(() => {
        // Find the Leaks button
        const leaksButton = document.querySelector('.tab-buttons button:nth-child(2)');
        if (leaksButton) {
          const rect = leaksButton.getBoundingClientRect();
          setButtonPosition({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
          setVisible(true);
        }
      }, 800);
    }
  }, [allowShow]);

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(SPOTLIGHT_DISMISS_KEY, 'true');
  };

  if (!visible || !buttonPosition) return null;

  return (
    <>
      {/* Dark overlay with cutout for button */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9998,
          animation: `${fadeIn} 0.4s ease-out`,
          pointerEvents: 'none',
        }}
      >
        {/* SVG mask approach for spotlight */}
        <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
          <defs>
            <mask id="spotlight-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={buttonPosition.left - 8}
                y={buttonPosition.top - 8}
                width={buttonPosition.width + 16}
                height={buttonPosition.height + 16}
                rx="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.75)"
            mask="url(#spotlight-mask)"
          />
        </svg>
      </Box>

      {/* Highlight ring around button */}
      <Box
        sx={{
          position: 'fixed',
          top: buttonPosition.top - 8,
          left: buttonPosition.left - 8,
          width: buttonPosition.width + 16,
          height: buttonPosition.height + 16,
          borderRadius: '8px',
          border: '3px solid #5568FE',
          zIndex: 9999,
          animation: `${pulse} 2s ease-in-out infinite`,
          pointerEvents: 'none',
        }}
      />

      {/* Clickable overlay to dismiss */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9997,
        }}
        onClick={handleDismiss}
      />

      {/* Explanation card positioned below the button */}
      <Card
        sx={{
          position: 'fixed',
          top: buttonPosition.top + buttonPosition.height + 20,
          left: Math.max(20, buttonPosition.left - 100),
          maxWidth: '380px',
          width: '380px',
          zIndex: 10000,
          p: 3,
          animation: `${slideIn} 0.5s ease-out 0.2s both`,
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                backgroundColor: '#5568FE',
                borderRadius: '50%',
                p: 1,
              }}
            >
              <RocketLaunchIcon sx={{ fontSize: 24, color: 'white' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Try Our New Past Papers Section!
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Access authentic SAT tests from past years. Practice with real past papers to boost your preparation.
          </Typography>

          <Box
            sx={{
              backgroundColor: '#f5f7fa',
              borderRadius: 1.5,
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              👆 Click the <strong>highlighted button</strong> above to explore
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDismiss}
              sx={{ px: 2 }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Card>
    </>
  );
};

export default LeaksSpotlight;
